export const PLATFORMS = [
  'Node.js',
  'Deno',
  'Bun',
  'Browser',
  'Web Worker',
  'Unknown',
] as const;

export type Platform = (typeof PLATFORMS)[number];

export const BROWSERS = [
  'Edge',
  'Opera',
  'Firefox',
  'Chrome',
  'Safari',
  'Internet Explorer',
  'Unknown Browser',
] as const;

export type BrowserName = (typeof BROWSERS)[number];

export type NodePlatform =
  | 'aix'
  | 'android'
  | 'cygwin'
  | 'darwin'
  | 'freebsd'
  | 'haiku'
  | 'linux'
  | 'netbsd'
  | 'openbsd'
  | 'sunos'
  | 'win32';

export type WebOperatingSystem =
  | 'Android'
  | 'iOS'
  | 'Windows'
  | 'MacOS'
  | 'Linux'
  | 'Unknown';

export type OperatingSystem = NodePlatform | WebOperatingSystem;

export interface BrowserInfo {
  readonly name: BrowserName;
  readonly version: string;
}

export interface ApiSupport {
  readonly serviceWorker: boolean;
  readonly webGL: boolean;
  readonly localStorage: boolean;
  readonly sessionStorage: boolean;
  readonly geolocation: boolean;
  readonly notifications: boolean;
  readonly fetch: boolean;
}

export interface NetworkInfo {
  readonly type: string;
  readonly downlink: number | null;
  readonly rtt: number | null;
  readonly saveData: boolean;
}

export interface EnvironmentInfo {
  readonly platform: Platform;
  readonly operatingSystem: OperatingSystem;
  readonly browser: BrowserName | null;
  readonly browserInfo: BrowserInfo | null;
  readonly apiSupport: ApiSupport | null;
  readonly networkInfo: NetworkInfo | null;
}

interface RuntimeGlobals {
  readonly process?: {
    readonly versions?: Readonly<Record<string, string | undefined>>;
    readonly platform?: NodePlatform;
  };
  readonly Deno?: { readonly version?: { readonly deno?: string } };
  readonly window?: Window & typeof globalThis;
  readonly WorkerGlobalScope?: abstract new (...args: never[]) => object;
  readonly self?: unknown;
  readonly navigator?: Navigator;
}

interface NetworkInformation {
  readonly effectiveType?: string;
  readonly downlink?: number | null;
  readonly rtt?: number | null;
  readonly saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformation;
  readonly mozConnection?: NetworkInformation;
  readonly webkitConnection?: NetworkInformation;
}

const runtimeGlobals = globalThis as unknown as RuntimeGlobals;


/** Returns whether the current runtime is Deno. */
export function isDeno(): boolean {
  return runtimeGlobals.Deno?.version?.deno != null;
}

/** Returns whether the current runtime is Bun. */
export function isBun(): boolean {
  return runtimeGlobals.process?.versions?.bun != null;
}

/**
 * Returns whether the current runtime is Node.js.
 *
 * Bun and Deno expose a Node-compatible `process` API, but they are separate
 * runtimes. Excluding their runtime markers prevents a false positive.
 */
export function isNode(): boolean {
  return (
    !isDeno() &&
    !isBun() &&
    runtimeGlobals.process?.versions?.node != null
  );
}

/** Returns whether the code is executing in a dedicated or shared Web Worker. */
export function isWebWorker(): boolean {
  const workerGlobalScope = runtimeGlobals.WorkerGlobalScope;
  const currentScope = runtimeGlobals.self;

  return (
    workerGlobalScope != null &&
    (typeof currentScope === 'object' || typeof currentScope === 'function') &&
    currentScope != null &&
    currentScope instanceof workerGlobalScope
  );
}

/** Returns whether the code is executing in a DOM browser environment. */
export function isBrowser(): boolean {
  return (
    runtimeGlobals.window?.document != null &&
    !isWebWorker()
  );
}

/** Detects the current runtime. */
export function detectPlatform(): Platform {
  if (isWebWorker()) return 'Web Worker';
  if (isDeno()) return 'Deno';
  if (isBun()) return 'Bun';
  if (isBrowser()) return 'Browser';
  if (isNode()) return 'Node.js';
  return 'Unknown';
}

function getNavigator(): Navigator | undefined {
  return runtimeGlobals.navigator;
}

/** Detects the operating system available in the current runtime. */
export function detectOperatingSystem(): OperatingSystem {
  if (isBrowser() || isWebWorker()) {
    return detectOSFromUserAgent(getNavigator()?.userAgent ?? '');
  }

  if (isNode() || isBun()) {
    return runtimeGlobals.process?.platform ?? 'Unknown';
  }

  return 'Unknown';
}

/**
 * Detects an operating system name from a user-agent string.
 * Mobile platforms are checked first because Android UA also contains Linux.
 */
export function detectOSFromUserAgent(
  userAgent: string,
): WebOperatingSystem {
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Windows NT/i.test(userAgent)) return 'Windows';
  if (/Mac OS X/i.test(userAgent)) return 'MacOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Unknown';
}

const BROWSER_PATTERNS: Readonly<Record<BrowserName, RegExp | undefined>> = {
  Edge: /Edg\/([\d.]+)/i,
  Opera: /(?:OPR|Opera)[/ ]([\d.]+)/i,
  Firefox: /Firefox\/([\d.]+)/i,
  Chrome: /Chrome\/([\d.]+)/i,
  Safari: /Version\/([\d.]+).*Safari\/[\d.]+/i,
  'Internet Explorer': /MSIE ([\d.]+)/i,
  'Unknown Browser': undefined,
};

/** Detects the current browser name, or `null` outside a DOM browser. */
export function detectBrowser(): BrowserName | null {
  if (!isBrowser()) return null;

  const userAgent = getNavigator()?.userAgent ?? '';

  if (BROWSER_PATTERNS.Edge?.test(userAgent)) return 'Edge';
  if (BROWSER_PATTERNS.Opera?.test(userAgent)) return 'Opera';
  if (BROWSER_PATTERNS.Firefox?.test(userAgent)) return 'Firefox';
  if (BROWSER_PATTERNS.Chrome?.test(userAgent)) return 'Chrome';
  if (BROWSER_PATTERNS.Safari?.test(userAgent)) return 'Safari';
  if (BROWSER_PATTERNS['Internet Explorer']?.test(userAgent)) {
    return 'Internet Explorer';
  }

  return 'Unknown Browser';
}

/** Detects the current browser name and version. */
export function detectBrowserInfo(): BrowserInfo | null {
  if (!isBrowser()) return null;

  const name = detectBrowser();
  if (name === null) return null;

  const userAgent = getNavigator()?.userAgent ?? '';
  const match = BROWSER_PATTERNS[name]?.exec(userAgent);

  return {
    name,
    version: match?.[1] ?? 'Unknown',
  };
}

/** Detects support for selected web APIs in a DOM browser. */
export function detectApiSupport(): ApiSupport | null {
  const currentWindow = runtimeGlobals.window;
  if (!isBrowser() || currentWindow == null) return null;

  const currentNavigator = getNavigator();
  if (currentNavigator == null) return null;

  return {
    serviceWorker: 'serviceWorker' in currentNavigator,
    webGL: 'WebGLRenderingContext' in currentWindow,
    localStorage: 'localStorage' in currentWindow,
    sessionStorage: 'sessionStorage' in currentWindow,
    geolocation: 'geolocation' in currentNavigator,
    notifications: 'Notification' in currentWindow,
    fetch: 'fetch' in currentWindow,
  };
}

/** Returns normalized Network Information API data when available. */
export function detectNetworkInfo(): NetworkInfo | null {
  if (!isBrowser()) return null;

  const currentNavigator = getNavigator() as NavigatorWithConnection | undefined;
  const connection =
    currentNavigator?.connection ??
    currentNavigator?.mozConnection ??
    currentNavigator?.webkitConnection;

  if (connection == null) return null;

  return {
    type: connection.effectiveType ?? 'unknown',
    downlink: connection.downlink ?? null,
    rtt: connection.rtt ?? null,
    saveData: connection.saveData ?? false,
  };
}

/** Returns all environment information supported by this module. */
export function getEnvironmentInfo(): EnvironmentInfo {
  return {
    platform: detectPlatform(),
    operatingSystem: detectOperatingSystem(),
    browser: detectBrowser(),
    browserInfo: detectBrowserInfo(),
    apiSupport: detectApiSupport(),
    networkInfo: detectNetworkInfo(),
  };
}
